/**
 * Contrast helpers for user-chosen brand colours — a Document Sign sender's
 * band, a Company's portal and sign-in (ADR-024 S10).
 *
 * Whoever picks the colour picks whatever their brand guide says. The
 * platform still has to render legible text on it, in both light and dark
 * themes, without knowing anything about the colour in advance — so
 * foreground colours are COMPUTED from the background rather than assumed.
 *
 * The bug this replaces: the call-to-action hard-coded white text on the
 * accent colour, which is unreadable on any light accent — including the
 * fairly light one in the Service Ventures palette.
 */

/** WCAG 2.1 relative luminance. */
export function relativeLuminance(hex: string): number {
  const value = hex.replace('#', '');
  const channel = (i: number): number => {
    const raw = parseInt(value.slice(i * 2, i * 2 + 2), 16) / 255;
    return raw <= 0.03928 ? raw / 12.92 : Math.pow((raw + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(0) + 0.7152 * channel(1) + 0.0722 * channel(2);
}

/** WCAG contrast ratio between two hex colours, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [lighter, darker] = la > lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Near-black rather than pure black: on a mid-tone brand colour it reads as
 * deliberate rather than as a rendering failure, and still clears AA.
 */
const ON_LIGHT = '#0A0A0A';
const ON_DARK = '#FFFFFF';

/** Text colour to place ON a background of `hex`, whichever contrasts more. */
export function onColor(hex: string): string {
  return contrastRatio(hex, ON_DARK) >= contrastRatio(hex, ON_LIGHT) ? ON_DARK : ON_LIGHT;
}

/**
 * Whether a colour is legible as TEXT against a page background.
 *
 * Used for the theme problem: a dark brand colour is fine as heading text in
 * light mode and close to invisible in dark mode. Rather than adjusting the
 * sender's colour — which would render a brand wrong — the caller falls back
 * to the theme's own foreground when this returns false, and keeps the brand
 * colour where it IS legible.
 */
export function isLegibleOn(color: string, background: string, minimumRatio = 4.5): boolean {
  return contrastRatio(color, background) >= minimumRatio;
}

/** Page background per theme, matching the app's own surfaces closely enough
 *  for a contrast decision. */
export const THEME_BACKGROUND = { light: '#FFFFFF', dark: '#0A0A0A' } as const;

/**
 * Minimum contrast between a sender-chosen header foreground and the band.
 *
 * MIRRORS `DS_BRAND_MIN_CONTRAST_RATIO` / `isLegibleHeaderTextColor` in
 * `@fias/db-types`, which `@fias/webapp-client` deliberately does not depend
 * on — the same arrangement as `assuranceTier.ts`. Pinned by
 * `tests/architecture/document-sign-brand-contrast-parity.test.ts`; change
 * both together.
 *
 * The server is the enforcer. This copy exists so the dialog can refuse a
 * colour while the sender is still looking at the field, rather than after a
 * round trip.
 */
export const BRAND_MIN_CONTRAST_RATIO = 4.5;

/** Is `textColor` legible on `bandColor`? No band means nothing to fail against. */
export function isLegibleHeaderTextColor(textColor: string, bandColor: string | null): boolean {
  if (!bandColor) return true;
  return contrastRatio(textColor, bandColor) >= BRAND_MIN_CONTRAST_RATIO;
}
